import { ChevronRight } from 'lucide-react';
import { Fragment } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { mytClock } from '../../utils/myt.js';
import { ringgitCents } from './dashboardSeries.js';
import type { TodaySummary } from './types.js';

/**
 * 355 (US1, design 2a): the Today column. Each count is a link to the list that shows exactly its rows (FR-011); the
 * server counts and the list filters with one rule, so the two cannot disagree.
 */
type CountKey = keyof TodaySummary['counts'];
type Tone = 'normal' | 'warn' | 'alarm' | 'zero';

interface RowSpec {
  key: CountKey;
  label: string;
  hint: string;
  to: string;
  /** The tone when the count is not zero. */
  tone: (platform: boolean) => Exclude<Tone, 'zero'>;
}

const ROWS: RowSpec[] = [
  {
    key: 'pickups_today',
    label: 'Pickups today',
    hint: 'confirmed, picked up today',
    to: '/bookings?bucket=pickups_today',
    tone: () => 'normal',
  },
  {
    key: 'returns_today',
    label: 'Returns today',
    hint: 'still due back today',
    to: '/bookings?bucket=returns_today',
    tone: () => 'normal',
  },
  {
    key: 'overdue',
    label: 'Overdue returns',
    hint: 'return time has passed',
    to: '/bookings?bucket=overdue',
    tone: () => 'alarm',
  },
  {
    key: 'payment_mismatch',
    label: 'Paid, waiting for staff',
    hint: 'amount differs: accept or cancel',
    to: '/bookings?bucket=payment_mismatch',
    // FR-025: the alarm colour is for the administrator, who decides; a host sees it as a warning.
    tone: (platform) => (platform ? 'alarm' : 'warn'),
  },
  {
    key: 'unpaid_holding',
    label: 'Unpaid, holding a slot',
    hint: 'awaiting payment',
    to: '/bookings?bucket=unpaid_holding',
    tone: () => 'warn',
  },
  // 416 (#1431, mock-up screen 3): the geo-fence's last reading; the list names each rental and its km outside.
  {
    key: 'outside_zone',
    label: 'Outside zone',
    hint: 'rented cars outside their pickup area now',
    to: '/bookings?bucket=outside_zone',
    tone: () => 'warn',
  },
  {
    key: 'identity_waiting',
    label: 'Identity checks waiting',
    hint: 'submitted or resubmitted',
    to: '/users?identity_status=awaiting',
    tone: () => 'warn',
  },
  {
    key: 'unbookable_cars',
    label: 'Cars not bookable today',
    hint: 'maintenance or blocked',
    to: '/cars?unbookable_today=true',
    tone: () => 'normal',
  },
];

const ROW_TONE: Record<Tone, string> = {
  normal: '',
  warn: '',
  alarm: 'bg-semantic-danger-bg/60',
  zero: '',
};
const DOT_TONE: Record<Tone, string> = {
  normal: 'bg-brand-blue-mid',
  warn: 'bg-brand-yellow',
  alarm: 'bg-semantic-danger-text',
  zero: 'bg-hairline-strong',
};
const COUNT_TONE: Record<Tone, string> = {
  normal: 'text-ink',
  warn: 'text-semantic-warning-text',
  alarm: 'text-semantic-danger-text',
  zero: 'text-muted',
};

const KIND_LABEL = { overdue: 'Overdue', pickup: 'Pickup', return: 'Return' } as const;
// 422 v2: this small text measured from the tokens (tests/styles/contrast.test.ts). The overdue pill reads charcoal on
// its red tint with a red ring in both themes (red on the tint was 4.11:1 in light, below AA); the pickup pill is
// brand-blue-mid in dark (primary-deep has no dark value: it read 1.98:1).
const KIND_PILL = {
  overdue: 'bg-semantic-danger-bg text-charcoal ring-1 ring-semantic-danger-text/45',
  pickup: 'bg-brand-blue-soft text-primary-deep dark:text-brand-blue-mid',
  return: 'bg-semantic-success-bg text-semantic-success-text',
} as const;

function MismatchRows({ data }: { data: TodaySummary }) {
  const more = data.counts.payment_mismatch - data.mismatch_rows.length;
  return (
    <div className="col-span-2 flex flex-col gap-1.5 rounded-lg bg-semantic-danger-bg/60 p-2 sm:rounded-none sm:px-4 sm:pt-0 sm:pb-3 sm:pl-9">
      {data.mismatch_rows.map((m) => (
        <div
          key={m.booking_id}
          data-testid={`mismatch-row-${m.booking_id}`}
          className="flex items-center justify-between gap-2 rounded-lg border border-semantic-danger-text/20 bg-canvas px-2.5 py-2 text-xs"
        >
          <span className="min-w-0">
            <span className="font-semibold text-ink">{m.plate ?? 'Unknown car'}</span>
            {m.first_name ? <span className="text-slate"> · {m.first_name}</span> : null}
            <span className="block text-slate">
              paid {m.received_minor === null ? 'nothing' : ringgitCents(m.received_minor)} of{' '}
              {m.due_minor === null ? '—' : ringgitCents(m.due_minor)}
            </span>
          </span>
          <Link
            to={`/bookings/${m.booking_id}`}
            className="shrink-0 font-semibold text-semantic-danger-text hover:underline"
          >
            Review →
          </Link>
        </div>
      ))}
      {more > 0 ? (
        <Link
          to="/bookings?bucket=payment_mismatch"
          className="text-xs font-medium text-semantic-danger-text hover:underline"
        >
          +{more} more
        </Link>
      ) : null}
    </div>
  );
}

export function TodayPanel({ data }: { data: TodaySummary }) {
  const platform = data.scope === 'platform';
  const rows = ROWS.filter((r) => data.counts[r.key] !== undefined);
  // 424: a host's page is one column (Today, Your money, This month); from `lg` the day's counts sit beside the next
  // pickups and returns, as on the platform page (422 v2), so the money is not pushed below the fold.
  return (
    <div className="flex flex-col gap-4 lg:grid lg:grid-cols-2 lg:items-start">
      <Card padding="none" className="overflow-hidden pt-3.5 pb-1.5">
        <h2 className="mx-4 mb-2.5 flex items-baseline justify-between">
          <span className="text-[11px] font-semibold tracking-wider text-steel uppercase">
            Today
          </span>
          <Link to="/schedule" className="text-sm font-medium text-primary hover:underline">
            Schedule →
          </Link>
        </h2>
        {/* FR-026 (design 2b): on a phone the counts are tiles, two per row; from `sm` up, a list. */}
        <div className="grid grid-cols-2 gap-2 border-t border-hairline-soft p-3 sm:block sm:border-t-0 sm:p-0">
          {rows.map((r) => {
            const n = data.counts[r.key] ?? 0;
            const tone: Tone = n === 0 ? 'zero' : r.tone(platform);
            return (
              <Fragment key={r.key}>
                <Link
                  to={r.to}
                  data-testid={`today-row-${r.key}`}
                  data-tone={tone}
                  className={`flex flex-col gap-0.5 rounded-lg border border-hairline p-3 hover:bg-surface sm:grid sm:grid-cols-[8px_minmax(0,1fr)_auto_14px] sm:items-center sm:gap-3 sm:rounded-none sm:border-0 sm:border-t sm:border-hairline-soft sm:px-4 sm:py-2.5 ${ROW_TONE[tone]} ${tone === 'alarm' ? 'border-semantic-danger-text/30' : ''}`}
                >
                  <span
                    className={`hidden h-2 w-2 rounded-full sm:block ${DOT_TONE[tone]}`}
                    aria-hidden="true"
                  />
                  <span className="flex min-w-0 flex-col">
                    <span
                      className={`text-[13px] font-medium sm:text-sm ${tone === 'alarm' ? 'text-semantic-danger-text' : 'text-charcoal'}`}
                    >
                      {r.label}
                    </span>
                    <span className="hidden text-xs text-steel sm:block">{r.hint}</span>
                  </span>
                  <span
                    className={`order-first text-2xl font-bold tabular-nums sm:order-none sm:text-[22px] ${COUNT_TONE[tone]}`}
                  >
                    {n}
                  </span>
                  <ChevronRight
                    className="hidden h-3.5 w-3.5 text-muted sm:block"
                    aria-hidden="true"
                  />
                </Link>
                {r.key === 'payment_mismatch' && n > 0 ? <MismatchRows data={data} /> : null}
              </Fragment>
            );
          })}
        </div>
      </Card>

      <NextCard data={data} />
    </div>
  );
}

/** 355: the next pickups and returns, the overdue ones first. 422: its own card, so the platform layout keeps it. */
export function NextCard({ data, raised = false }: { data: TodaySummary; raised?: boolean }) {
  return (
    <Card
      padding="none"
      className={`overflow-hidden pt-3.5 pb-1.5 ${raised ? 'dark:border-hairline-strong dark:bg-surface' : ''}`}
      data-testid="next-card"
    >
      {raised ? (
        // 422 v2 (design frames 1-4): a card title like Today's, beside it in the Today section.
        <h2 className="mx-4 mb-2.5 flex flex-wrap items-baseline justify-between gap-2">
          <span className="text-[15px] font-bold text-ink">Next pickups and returns</span>
          <span className="text-xs text-steel dark:text-slate">next 6 hours · overdue first</span>
        </h2>
      ) : (
        <h2 className="mx-4 mb-2.5 text-[11px] font-semibold tracking-wider text-steel uppercase">
          Next pickups and returns
        </h2>
      )}
      {data.next.length === 0 ? (
        <p
          data-testid="next-empty"
          className="border-t border-hairline-soft px-4 py-3 text-sm text-steel dark:text-slate"
        >
          Nothing due in the next few hours.
        </p>
      ) : (
        data.next.map((r) => (
          <Link
            key={`${r.kind}-${r.booking_id}`}
            to={`/bookings/${r.booking_id}`}
            data-testid={`next-row-${r.booking_id}`}
            className={`grid grid-cols-[44px_minmax(0,1fr)_auto] items-center gap-2.5 border-t border-hairline-soft px-4 py-2 text-sm hover:bg-surface ${r.kind === 'overdue' ? 'bg-semantic-danger-bg/50' : ''}`}
          >
            <span className="font-semibold tabular-nums text-ink">{mytClock(r.at)}</span>
            <span className="truncate text-slate">
              <span className="font-semibold text-ink">{r.plate ?? 'Unknown car'}</span>
              {r.first_name ? ` · ${r.first_name}` : ''}
            </span>
            <span className={`rounded px-2 py-0.5 text-xs font-medium ${KIND_PILL[r.kind]}`}>
              {KIND_LABEL[r.kind]}
            </span>
          </Link>
        ))
      )}
    </Card>
  );
}

/**
 * 422 (#1526, design frames 1-2): the platform's compact Today card. The day's counts that the live events above do not
 * already show (overdue returns and identity checks are there), each a link to the list holding exactly its rows; two
 * across beside the SuperAdmin's month, three across in the Admin layout. A host keeps `TodayPanel`.
 */
const CARD_KEYS: CountKey[] = [
  'pickups_today',
  'returns_today',
  'payment_mismatch',
  'unpaid_holding',
  'outside_zone',
  'unbookable_cars',
];

export function TodayCard({ data, columns = 2 }: { data: TodaySummary; columns?: 1 | 2 | 3 }) {
  const platform = data.scope === 'platform';
  const rows = CARD_KEYS.map((k) => ROWS.find((r) => r.key === k)).filter(
    (r): r is RowSpec => r !== undefined && data.counts[r.key] !== undefined,
  );
  return (
    <Card
      padding="none"
      className="overflow-hidden p-4 dark:border-hairline-strong dark:bg-surface"
      data-testid="today-card"
    >
      {/* 422 v2: the Schedule link moved up to the Today section's heading. */}
      <h2 className="mb-3 text-[15px] font-bold text-ink">Today</h2>
      <div
        data-testid="today-card-grid"
        className={`grid gap-2.5 ${columns === 1 ? 'grid-cols-1' : columns === 3 ? 'grid-cols-2 sm:grid-cols-3' : 'grid-cols-2'}`}
      >
        {rows.map((r) => {
          const n = data.counts[r.key] ?? 0;
          const tone: Tone = n === 0 ? 'zero' : r.tone(platform);
          return (
            <Link
              key={r.key}
              to={r.to}
              data-testid={`today-card-${r.key}`}
              data-tone={tone}
              title={r.hint}
              className={`flex items-baseline justify-between gap-2 rounded-lg border px-3 py-2.5 hover:bg-surface dark:hover:bg-surface-soft ${
                tone === 'alarm'
                  ? 'border-semantic-danger-text/30 bg-semantic-danger-bg'
                  : 'border-hairline-soft dark:border-hairline'
              }`}
            >
              {/* 422 v2: an alarm's label is charcoal on its tint (12.2:1) and its count large red (20 px bold). */}
              <span
                className={`min-w-0 text-[13px] ${tone === 'alarm' ? 'font-medium text-charcoal' : 'text-slate'}`}
              >
                {r.label}
              </span>
              <b
                className={`${tone === 'alarm' ? 'text-xl' : 'text-lg'} tabular-nums ${COUNT_TONE[tone]}`}
              >
                {n}
              </b>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
